import { IgAttributionRow } from '../api/IgApi';

/**
 * Pure data model for the feature-attribution heatmap. Kept free of React /
 * Cloudscape imports so it can be unit-tested directly.
 */

// Non-feature summary rows emitted by run_ig.py (BASELINE/RECONSTRUCTED/ACTUAL
// FORECAST are reconstruction diagnostics; GAP* is the IG convergence gap). These
// are not features and have no place on a feature heatmap; the Table shows them.
export const SUMMARY_FEATURES = ['BASELINE', 'RECONSTRUCTED', 'ACTUAL FORECAST'];

// RdBu_r colormap stops (matplotlib), t in [-1, 1]: blue (neg) -> white -> red (pos).
export const RDBU_STOPS: Array<[number, [number, number, number]]> = [
  [-1.0, [5, 48, 97]],
  [-0.5, [146, 197, 222]],
  [0.0, [247, 247, 247]],
  [0.5, [244, 165, 130]],
  [1.0, [103, 0, 31]],
];

export type Metric = 'p50_score' | 'p90_score';

export interface Feature {
  name: string;
  isStatic: boolean;
}

export interface HeatmapModel {
  features: Feature[];
  offsets: number[];
  offsetDate: Map<number, string>;
  temporalAt: Map<string, number>; // key = tkey(feature, offset)
  staticAt: Map<string, number>; // feature -> single static attribution value
  hasStatic: boolean;
  vmax: number;
  rowVmax: Map<string, number>; // per-feature max|value| for row normalization
  fcdIndex: number; // index into offsets of first future column, -1 if none
}

export const tkey = (f: string, o: number): string => `${f}\u0000${o}`;

/** Map an attribution value to an RdBu_r rgb() string, normalized by vmax. */
export function rdbuColor(value: number, vmax: number): string {
  if (vmax === 0 || !isFinite(value)) return 'rgb(247,247,247)';
  const t = Math.max(-1, Math.min(1, value / vmax));
  for (let i = 0; i < RDBU_STOPS.length - 1; i++) {
    const [t0, c0] = RDBU_STOPS[i];
    const [t1, c1] = RDBU_STOPS[i + 1];
    if (t >= t0 && t <= t1) {
      const f = (t - t0) / (t1 - t0);
      const r = Math.round(c0[0] + (c1[0] - c0[0]) * f);
      const g = Math.round(c0[1] + (c1[1] - c0[1]) * f);
      const b = Math.round(c0[2] + (c1[2] - c0[2]) * f);
      return `rgb(${r},${g},${b})`;
    }
  }
  return 'rgb(247,247,247)';
}

export function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.min(
    sortedAsc.length - 1,
    Math.max(0, Math.round((p / 100) * (sortedAsc.length - 1)))
  );
  return sortedAsc[idx];
}

/**
 * Build the heatmap model from raw IG attribution rows.
 *
 * - Excludes only the non-feature summary rows (BASELINE/RECONSTRUCTED/ACTUAL
 *   FORECAST/GAP*). Every real feature — static and temporal — is kept, so the
 *   heatmap covers whatever the Table covers, regardless of baseline.
 * - Static features (static: prefix or non-finite day_offset) get a single
 *   value in `staticAt`; temporal features get per-offset values in `temporalAt`.
 * - A name is temporal if ANY of its rows is temporal.
 * - FCD boundary uses the date-based split (date >= fcd), falling back to the
 *   first day_offset >= 0.
 */
export function buildModel(
  rows: IgAttributionRow[],
  metric: Metric,
  fcd?: string,
  maxFeatures?: number
): HeatmapModel {
  const featureRows = rows.filter(
    (r) => !SUMMARY_FEATURES.includes(r.feature) && !r.feature.startsWith('GAP')
  );

  const temporalAt = new Map<string, number>();
  const staticAt = new Map<string, number>();
  const importance = new Map<string, number>();
  const isStatic = new Map<string, boolean>();
  const offsetSet = new Set<number>();
  const offsetDate = new Map<number, string>();

  for (const r of featureRows) {
    const staticRow = r.feature.startsWith('static:') || !isFinite(Number(r.day_offset));
    const name = r.feature.replace(/^static:/, '');
    const score = Number(r[metric]) || 0;

    if (!isStatic.has(name)) isStatic.set(name, staticRow);
    else if (!staticRow) isStatic.set(name, false);

    if (staticRow) {
      staticAt.set(name, (staticAt.get(name) ?? 0) + score);
      importance.set(name, (importance.get(name) ?? 0) + Math.abs(score));
    } else {
      // v2 per-quantile top-day filter: when the selected metric's rank column
      // exists, keep only days in THAT quantile's top-N (so a P90 heatmap shows
      // P90's own top days, not the P50-selected ones). Pre-v2 CSVs lack the
      // column entirely -> include every temporal row (prior behavior).
      const rankField = metric === 'p90_score' ? 'p90_rank' : 'p50_rank';
      const rankVal = r[rankField];
      const inTop =
        rankVal === undefined ? true : rankVal !== '' && isFinite(Number(rankVal));
      if (!inTop) continue;

      const offset = Number(r.day_offset);
      temporalAt.set(tkey(name, offset), (temporalAt.get(tkey(name, offset)) ?? 0) + score);
      offsetSet.add(offset);
      if (r.date) offsetDate.set(offset, r.date);
      importance.set(name, (importance.get(name) ?? 0) + Math.abs(score));
    }
  }

  let ordered = [...importance.entries()].sort((a, b) => b[1] - a[1]);
  if (maxFeatures && isFinite(maxFeatures)) ordered = ordered.slice(0, maxFeatures);

  const features: Feature[] = ordered.map(([name]) => ({
    name,
    isStatic: isStatic.get(name) ?? false,
  }));

  const offsets = [...offsetSet].sort((a, b) => a - b);
  const hasStatic = features.some((f) => f.isStatic);

  const absVals: number[] = [];
  for (const f of features) {
    if (f.isStatic) {
      const v = staticAt.get(f.name);
      if (v != null && isFinite(v)) absVals.push(Math.abs(v));
    } else {
      for (const o of offsets) {
        const v = temporalAt.get(tkey(f.name, o));
        if (v != null && isFinite(v)) absVals.push(Math.abs(v));
      }
    }
  }
  absVals.sort((a, b) => a - b);
  const vmax = percentile(absVals, 99) || (absVals.length ? absVals[absVals.length - 1] : 1);

  let fcdIndex = -1;
  if (fcd) {
    const idx = offsets.findIndex((o) => {
      const d = offsetDate.get(o);
      return d != null && String(d).slice(0, 10) >= fcd;
    });
    if (idx > 0) fcdIndex = idx;
  }
  if (fcdIndex < 0) {
    const firstNonNeg = offsets.findIndex((o) => o >= 0);
    if (firstNonNeg > 0) fcdIndex = firstNonNeg;
  }

  // Per-feature (row) color scale: max |value| within each feature's own cells.
  // Lets a "per-feature" view reveal small features that the shared 99th-pct
  // vmax washes out (e.g. when one feature dominates).
  const rowVmax = new Map<string, number>();
  for (const f of features) {
    let m = 0;
    if (f.isStatic) {
      const v = staticAt.get(f.name);
      if (v != null && isFinite(v)) m = Math.abs(v);
    } else {
      for (const o of offsets) {
        const v = temporalAt.get(tkey(f.name, o));
        if (v != null && isFinite(v)) m = Math.max(m, Math.abs(v));
      }
    }
    rowVmax.set(f.name, m || vmax || 1);
  }

  return { features, offsets, offsetDate, temporalAt, staticAt, hasStatic, vmax, rowVmax, fcdIndex };
}
